import { defineStore } from 'pinia'
import { ref } from 'vue'

import { usePopupAnalytics } from '@/composables/use-popup-analytics'
import { CLIENT_QUERY } from '@/router/query-params'
import { useLoggerStore } from '@/store/logger'
import { useNotificationsQueue } from '@/store/notification-que/notifications-queue'
import {
    type BrowserHandoff,
    detectInAppBrowser,
    openBrowserHandoff,
    requestBrowserHandoff,
} from '@/utils/in-app-browser'
import safeURLParse from '@/utils/safeURLParse'

const HANDOFF_FALLBACK_DELAY_MS = 1500

export const useInAppBrowserStore = defineStore(
    'in_app_browser',
    () => {
        const handoff = ref<BrowserHandoff>()
        // stays true after a dismissal so the modal can play its leave transition and restore the scroll
        const isFallbackMounted = ref(false)
        const isFallbackOpened = ref(false)
        // persisted: a dismissal rules this browser out for good, handoff included
        const isDismissed = ref(false)
        const popupAnalytics = usePopupAnalytics('open-in-browser-modal')
        const loggerStore = useLoggerStore()

        let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null
        let pageWasHidden = false
        let hasLoggedEscape = false

        function logEscapeAttempt(parentApp: BrowserHandoff['parentApp'], trigger: 'auto' | 'manual') {
            loggerStore.logEvent(
                {
                    event: 'custom_event',
                    eventName: 'try-escape-webview',
                    dimension1: parentApp,
                    dimension2: trigger,
                },
                { flushImmediately: true },
            )
        }

        function clearFallbackTimeout() {
            if (fallbackTimeoutId) {
                clearTimeout(fallbackTimeoutId)
                fallbackTimeoutId = null
            }
        }

        // Once the app loses focus, the fallback must not nag on return.
        function onVisibilityChange() {
            if (document.visibilityState === 'hidden') {
                pageWasHidden = true
                clearFallbackTimeout()
            }
        }

        function showFallback() {
            fallbackTimeoutId = null
            if (pageWasHidden || document.visibilityState !== 'visible') {
                return
            }

            // the handoff is the only thing worth doing in a web view, and our <dialog> sits in the
            // top layer above every prompt anyway - so hold the queue instead of queueing behind it
            useNotificationsQueue().setSuppressed(true, 'open-in-browser')
            isFallbackMounted.value = true
            isFallbackOpened.value = true
            popupAnalytics.shown({ trigger: handoff.value?.method || 'unknown' })
        }

        function init() {
            const targetUrl = safeURLParse(window.location.href)
            if (!targetUrl) {
                return
            }
            const escapedFrom = targetUrl.searchParams.get(CLIENT_QUERY.ESCAPED_FROM_WEBVIEW)
            if (escapedFrom) {
                if (!hasLoggedEscape) {
                    hasLoggedEscape = true
                    loggerStore.logEvent({
                        event: 'custom_event',
                        eventName: 'escaped-webview',
                        dimension1: escapedFrom,
                    })
                }
                return
            }
            if (isDismissed.value || handoff.value) {
                return
            }
            const resolved = detectInAppBrowser(
                window,
                targetUrl,
                targetUrl.searchParams.get(CLIENT_QUERY.FORCE_HANDOFF) === '1',
            )
            if (!resolved) {
                return
            }

            handoff.value = resolved
            document.addEventListener('visibilitychange', onVisibilityChange)
            logEscapeAttempt(resolved.parentApp, 'auto')
            requestBrowserHandoff(resolved)
            fallbackTimeoutId = setTimeout(showFallback, HANDOFF_FALLBACK_DELAY_MS)
        }

        function openFallbackHandoff() {
            popupAnalytics.clicked('open-in-browser')

            if (!handoff.value) {
                return
            }
            logEscapeAttempt(handoff.value.parentApp, 'manual')
            if (handoff.value.method === 'meta-refresh' || handoff.value.method === 'intent') {
                openBrowserHandoff(handoff.value)
            }
        }

        function dismissFallback() {
            if (!isFallbackOpened.value) {
                return
            }
            popupAnalytics.closed('close-button')
            isFallbackOpened.value = false
            isDismissed.value = true
            useNotificationsQueue().setSuppressed(false, 'open-in-browser')
        }

        function dispose() {
            clearFallbackTimeout()
            document.removeEventListener('visibilitychange', onVisibilityChange)
            useNotificationsQueue().setSuppressed(false, 'open-in-browser')
        }

        return {
            handoff,
            isFallbackMounted,
            isFallbackOpened,
            isDismissed,
            init,
            dispose,
            dismissFallback,
            openFallbackHandoff,
        }
    },
    {
        persist: {
            key: 'in_app_browser',
            pick: ['isDismissed'],
        },
    },
)
